import React, { useEffect, useState } from 'react';
import { styles } from './SellerCollection.css';
import { MarketplaceSeller } from '../../Marketplace/factories/marketplaceSellerFactory';
import { SellerProfile } from '../../Marketplace/SellerProfile/SellerProfile';
import { BreadcrumbItemProps, SimpleBreadcrumbs } from '../../SimpleBreadcrumbs/SimpleBreadcrumbs';
import { CarouselTabs } from '../../CarouselTabs/CarouselTabs';
import { MinimalTab } from '../../CarouselTabs/MinimalTab/MinimalTab';
import { ProductsTab, ReturnsRefundsTab, ShippingTab } from './tabs';
import { Disclaimer } from '../../Disclaimer';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { SITE_MAX_WIDTH_PX } from '../../styles/constants';

const SELLER_COLLECTION_BASE_BREADCRUMBS: Array<BreadcrumbItemProps> = [
  { label: 'Home', isLink: true, href: '/' },
  { label: 'Marketplace', href: '/pages/marketplace' },
];

export type SellerCollectionProps = {
  seller: MarketplaceSeller;
  disclaimers?: Array<string>;
  productsTabContents: React.ReactNode;
};

export const SellerCollection = ({ seller, disclaimers, productsTabContents }: SellerCollectionProps) => {
  const [windowSize, setWindowSize] = useState(window.innerWidth ?? 0);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth ?? 0);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.headerOuter}>
        <div className={styles.breadcrumbsContainer}>
          <SimpleBreadcrumbs breadcrumbs={[...SELLER_COLLECTION_BASE_BREADCRUMBS, { label: seller.name }]} />
        </div>
        <div className={styles.headerInner}>
          <SellerProfile
            name={seller.name}
            subTitle="JB Hi-Fi Marketplace seller"
            logoSrc={seller.logo}
            description={seller.description}
          />
        </div>
      </div>
      <CarouselTabs
        tabsContainerClassName={styles.tabsContainer}
        slidesOffsetBefore={Math.max(0, (windowSize - SITE_MAX_WIDTH_PX) / 2)}
        hideGradients={false}
        gradientColor={palette.secondary.main}
        tabs={[
          {
            renderLabel: tabProps => <MinimalTab label="Products" {...tabProps} />,
            contents: <ProductsTab>{productsTabContents}</ProductsTab>,
            labelTestId: 'products-tab-label',
            contentsTestId: 'products-tab-content',
          },
          {
            renderLabel: tabProps => <MinimalTab label="Shipping" {...tabProps} />,
            contents: <ShippingTab shippingMarkdown={seller.shippingInfo} />,
            labelTestId: 'shipping-tab-label',
            contentsTestId: 'shipping-tab-content',
          },
          {
            renderLabel: tabProps => <MinimalTab label="Returns & Refunds policy" {...tabProps} />,
            contents: <ReturnsRefundsTab returnsRefundsMarkdown={seller.returnsRefundsPolicy} />,
            labelTestId: 'refunds-tab-label',
            contentsTestId: 'refunds-tab-content',
          },
        ]}
      />
      {disclaimers ? (
        <div className={styles.disclaimersContainer}>
          <Disclaimer disclaimers={disclaimers} />
        </div>
      ) : null}
    </div>
  );
};
